import { useEffect, useId, useRef, useState } from 'react';
import { examples } from '../lib/examples';
import { Icon } from './Icon';

interface Props {
	disabled: boolean;
	onFile: (file: File) => void;
	onExample: (id: string) => void;
}

export function LoadMenu({ disabled, onFile, onExample }: Props) {
	const [open, setOpen] = useState(false);
	const root = useRef<HTMLDivElement>(null);
	const trigger = useRef<HTMLButtonElement>(null);
	const input = useRef<HTMLInputElement>(null);
	const id = useId();

	useEffect(() => {
		if (disabled) setOpen(false);
	}, [disabled]);

	useEffect(() => {
		if (!open) return;
		const dismiss = (event: PointerEvent) => {
			if (!root.current?.contains(event.target as Node)) setOpen(false);
		};
		document.addEventListener('pointerdown', dismiss);
		return () => document.removeEventListener('pointerdown', dismiss);
	}, [open]);

	function close() {
		setOpen(false);
		trigger.current?.focus();
	}

	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: container handles Escape and focus containment for the dropdown menu; the trigger button is the interactive control
		<div
			className="load-menu"
			ref={root}
			onBlur={(event) => {
				if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
			}}
			onKeyDown={(event) => {
				if (event.key === 'Escape' && open) {
					event.preventDefault();
					event.stopPropagation();
					close();
				}
			}}
		>
			<button
				type="button"
				className="load-button"
				ref={trigger}
				disabled={disabled}
				aria-expanded={open}
				aria-controls={id}
				onClick={() => setOpen(!open)}
			>
				<Icon name="folder-open" size={14} /> Load <Icon name="chevron-down" size={12} />
			</button>
			<input
				ref={input}
				type="file"
				hidden
				accept=".c,.h,.txt,text/plain,text/x-c"
				aria-label="Load C source file"
				disabled={disabled}
				onChange={(event) => {
					const file = event.currentTarget.files?.[0];
					event.currentTarget.value = '';
					if (file) onFile(file);
				}}
			/>
			{open && (
				<div className="load-options" id={id}>
					<button
						type="button"
						disabled={disabled}
						onClick={() => {
							close();
							input.current?.click();
						}}
					>
						<Icon name="folder-open" size={14} /> From your computer…
					</button>
					<fieldset className="load-examples" aria-label="Examples">
						<span className="load-options-label">Examples</span>
						{examples.map((example) => (
							<button
								type="button"
								key={example.id}
								disabled={disabled}
								onClick={() => {
									close();
									onExample(example.id);
								}}
							>
								{example.name}
							</button>
						))}
					</fieldset>
				</div>
			)}
		</div>
	);
}
